import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause, X, SkipForward } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

function beep(freq = 880, duration = 0.5) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch {}
}

export default function Pomodoro() {
  const { pomodoro, pausePomodoro, resumePomodoro, stopPomodoro, togglePomodoroPhase, setActiveView } = useApp();
  const [, setNow] = useState(Date.now());
  const lastPhaseRef = useRef(null);

  useEffect(() => {
    if (!pomodoro || pomodoro.isPaused) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [pomodoro]);

  // Auto-switch phase on zero
  useEffect(() => {
    if (!pomodoro || pomodoro.isPaused) return;
    const remaining = Math.max(0, Math.floor((pomodoro.endsAt - Date.now()) / 1000));
    if (remaining <= 0 && lastPhaseRef.current !== pomodoro.phase) {
      lastPhaseRef.current = pomodoro.phase;
      beep(pomodoro.phase === 'work' ? 880 : 660);
      togglePomodoroPhase();
    }
  });

  if (!pomodoro) return null;

  const remaining = pomodoro.isPaused
    ? pomodoro.remainingSec
    : Math.max(0, Math.floor((pomodoro.endsAt - Date.now()) / 1000));
  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;
  const isWork = pomodoro.phase === 'work';

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 1500,
      background: 'var(--bg-1)', border: `2px solid ${isWork ? 'var(--accent)' : 'var(--green)'}`,
      borderRadius: 'var(--radius-lg)', padding: 14, minWidth: 240,
      boxShadow: 'var(--shadow-lg)', fontFamily: 'var(--font-sans)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 16 }}>{isWork ? '🍅' : '☕'}</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>{isWork ? 'Fokuszeit' : 'Pause'}</span>
        <button onClick={stopPomodoro} className="btn btn-icon btn-ghost btn-sm" title="Beenden"><X size={12} /></button>
      </div>
      <div
        onClick={() => setActiveView && setActiveView('tasks')}
        style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4, cursor: 'default',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={pomodoro.taskTitle}
      >
        {pomodoro.taskTitle}
      </div>
      <div style={{
        fontSize: 36, fontWeight: 300, fontVariantNumeric: 'tabular-nums',
        textAlign: 'center', color: isWork ? 'var(--accent)' : 'var(--green)', margin: '4px 0',
      }}>
        {min}:{String(sec).padStart(2, '0')}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
        {pomodoro.isPaused ? (
          <button onClick={resumePomodoro} className="btn btn-ghost btn-sm" title="Fortsetzen">
            <Play size={12} /> Weiter
          </button>
        ) : (
          <button onClick={pausePomodoro} className="btn btn-ghost btn-sm" title="Pausieren">
            <Pause size={12} /> Pause
          </button>
        )}
        <button onClick={togglePomodoroPhase} className="btn btn-ghost btn-sm" title="Zur nächsten Phase springen">
          <SkipForward size={12} /> {isWork ? 'Pause' : 'Fokus'}
        </button>
      </div>
    </div>
  );
}
